import {useEffect, useState} from "react";
import {AVATAR_ELEMENT_TYPES, AvatarCatalogue, AvatarElementType, OPTIONAL_AVATAR_TYPES} from "../../models/avatars.model";
import TintedPreview from "./tintedPreview.element";

// Composite preview: the five layer types stacked in draw order (face, eyes,
// facial hair, hair, hat), each tinted a random color — a quick "what will
// players actually get" check. Purely local state; nothing here touches the
// catalogue.

const COMPOSITE_SIZE = 192;

interface LayerPick {
    fileName: string;
    color: string;
}

function randomHexColor(): string {
    return '#' + Math.floor(Math.random() * 0x1000000).toString(16).padStart(6, '0');
}

// Mirror of the client's rule: pick from the type's palette when one is
// authored, otherwise any random color.
function rollColor(palette: string[]): string {
    if (palette.length === 0) return randomHexColor();
    return '#' + palette[Math.floor(Math.random() * palette.length)];
}

function AvatarComposite(props: {catalogue: AvatarCatalogue}) {
    const [picks, setPicks] = useState<Partial<Record<AvatarElementType, LayerPick>>>({});

    function randomize() {
        const next: Partial<Record<AvatarElementType, LayerPick>> = {};
        for (const type of AVATAR_ELEMENT_TYPES) {
            const pool = props.catalogue.elements[type];
            if (pool.length === 0) continue;
            // Optional slots (facial hair, hat) roll "none" as one extra option —
            // the layer is simply skipped, like a player avatar omitting it.
            if (OPTIONAL_AVATAR_TYPES.includes(type) && Math.random() < 1 / (pool.length + 1)) continue;
            const element = pool[Math.floor(Math.random() * pool.length)];
            next[type] = {fileName: element.fileName, color: rollColor(props.catalogue.palettes[type])};
        }
        setPicks(next);
    }

    // Roll an initial look, and re-roll when the catalogue changes so the
    // preview never keeps showing a just-deleted element.
    useEffect(() => {
        randomize();
        // randomize isn't a stable identity; the catalogue is the real trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.catalogue]);

    const empty = (['face', 'eyes', 'hair'] as const)
        .every(type => props.catalogue.elements[type].length === 0);

    return (
        <div className={'layout horizontal center gap'} style={{gap: 16}}>
            {/* Dark backing behind the whole stack so light tints read. */}
            <div style={{
                position: 'relative', width: COMPOSITE_SIZE, height: COMPOSITE_SIZE,
                backgroundColor: '#21262d', borderRadius: 8, flexShrink: 0,
            }}>
                {AVATAR_ELEMENT_TYPES.map(type => {
                    const pick = picks[type];
                    if (!pick) return null;
                    return (
                        <div key={type} style={{position: 'absolute', top: 0, left: 0}}>
                            <TintedPreview fileName={pick.fileName} color={pick.color} size={COMPOSITE_SIZE}/>
                        </div>
                    );
                })}
            </div>
            <div className={'layout vertical gap'} style={{gap: 8, alignItems: 'flex-start'}}>
                <button onClick={randomize}>
                    <span className={'fa-solid fa-dice'}/> Randomize
                </button>
                {empty &&
                    <p className={'muted-text'}>
                        Upload face, eyes and hair elements below to preview a composite avatar.
                    </p>}
            </div>
        </div>
    );
}

export default AvatarComposite;
